import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../shared/services/toast.service';
import { ChatTeamAdminApiService } from '../../services/chat-team-admin-api.service';
import { AgentLookupApiService } from '../../services/agent-lookup-api.service';
import { AgentLookupResponse, agentDisplayLabel } from '../../models/agent-lookup.model';
import { ChatTeamMemberAdmin } from '../../models/chat-team-admin.model';

/**
 * Alta/edición de un miembro del equipo de chat.
 *
 * Los campos de reparto no son decorativos: `routingWeight` es lo que hace que el modo Turnos
 * reparta desigual, `maxConcurrentChats` es lo que mira Menor carga para saber quién está libre,
 * y los idiomas deciden a quién puede ir una conversación en cada idioma.
 */
@Component({
  selector: 'app-chat-team-member-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  template: `
    <div class="p-6 space-y-4">
      @if (error) {
        <div class="p-3 bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 rounded-lg text-sm text-error-700 dark:text-error-400">{{ error }}</div>
      }
      <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Usuario <span class="text-error-500">*</span>
          </label>
          <select formControlName="userId"
            class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 disabled:opacity-60">
            <option [ngValue]="null">— Selecciona un usuario —</option>
            @for (a of agentes; track a.id) { <option [ngValue]="a.id">{{ agentLabel(a) }}</option> }
          </select>
          @if (hasError('userId')) {
            <p class="mt-1 text-xs text-error-600 dark:text-error-400">Selecciona el usuario que se une al equipo.</p>
          }
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Peso de reparto</label>
            <input type="number" min="1" max="100" formControlName="routingWeight"
              class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500" />
            <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">A mayor peso, más turnos recibe.</p>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Chats simultáneos</label>
            <input type="number" min="1" max="100" formControlName="maxConcurrentChats"
              class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500" />
            <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Tope que usa el modo Menor carga.</p>
          </div>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Idiomas que atiende</label>
          <div class="flex flex-wrap gap-2">
            @for (idioma of idiomas; track idioma.code) {
              <button type="button" (click)="toggleIdioma(idioma.code)"
                class="rounded-full border px-3 py-1 text-xs font-medium transition"
                [class.border-brand-500]="tieneIdioma(idioma.code)"
                [class.bg-brand-50]="tieneIdioma(idioma.code)"
                [class.text-brand-600]="tieneIdioma(idioma.code)"
                [class.dark:bg-brand-500/10]="tieneIdioma(idioma.code)"
                [class.dark:text-brand-400]="tieneIdioma(idioma.code)"
                [class.border-gray-300]="!tieneIdioma(idioma.code)"
                [class.text-gray-600]="!tieneIdioma(idioma.code)"
                [class.dark:border-gray-600]="!tieneIdioma(idioma.code)"
                [class.dark:text-gray-300]="!tieneIdioma(idioma.code)">
                {{ idioma.label }}
              </button>
            }
          </div>
          <p class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Sin ninguno marcado atiende en cualquier idioma.</p>
        </div>

        <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" formControlName="canReceiveLiveChat" class="rounded border-gray-300 dark:border-gray-600" />
          Recibe chat en vivo
        </label>
        <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" formControlName="canReceiveBotEscalation" class="rounded border-gray-300 dark:border-gray-600" />
          Recibe conversaciones que escala el bot
        </label>
        <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" formControlName="active" class="rounded border-gray-300 dark:border-gray-600" />
          Miembro activo
        </label>

        <div class="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <app-button variant="outline" type="button" (btnClick)="cancelar()">Cancelar</app-button>
          <app-button type="button" [disabled]="saving" (btnClick)="guardar()">
            @if (saving) { Guardando… } @else { {{ isEditing ? 'Actualizar' : 'Agregar' }} }
          </app-button>
        </div>
      </form>
    </div>
  `
})
export class ChatTeamMemberFormComponent implements OnDestroy {
  @Output() saved = new EventEmitter<ChatTeamMemberAdmin>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private api = inject(ChatTeamAdminApiService);
  private agentLookup = inject(AgentLookupApiService);
  private toast = inject(ToastService);

  readonly agentLabel = agentDisplayLabel;
  readonly idiomas = [
    { code: 'es', label: 'Español' },
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'it', label: 'Italiano' }
  ];

  agentes: AgentLookupResponse[] = [];
  idiomasSeleccionados: string[] = [];
  teamId: number | null = null;
  memberId: number | null = null;
  isEditing = false;
  saving = false;
  error: string | null = null;

  form: FormGroup = this.fb.group({
    userId: [null as number | null, Validators.required],
    routingWeight: [1],
    maxConcurrentChats: [5],
    canReceiveLiveChat: [true],
    canReceiveBotEscalation: [true],
    active: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(teamId: number): void {
    this.teamId = teamId;
    this.memberId = null;
    this.isEditing = false;
    this.error = null;
    this.saving = false;
    this.idiomasSeleccionados = [];
    this.form.reset({
      userId: null, routingWeight: 1, maxConcurrentChats: 5,
      canReceiveLiveChat: true, canReceiveBotEscalation: true, active: true
    });
    this.form.get('userId')?.enable();
    this.cargarAgentes();
  }

  loadMember(teamId: number, memberId: number): void {
    this.teamId = teamId;
    this.memberId = memberId;
    this.isEditing = true;
    this.cargarAgentes();
    this.api.getMembers(teamId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (miembros) => {
        const member = miembros.find((m) => m.id === memberId);
        if (!member) {
          this.error = 'No encontramos ese miembro en el equipo.';
          return;
        }
        this.idiomasSeleccionados = [...(member.languageCodes ?? [])];
        this.form.patchValue({
          userId: member.userId,
          routingWeight: member.routingWeight,
          maxConcurrentChats: member.maxConcurrentChats,
          canReceiveLiveChat: member.canReceiveLiveChat,
          canReceiveBotEscalation: member.canReceiveBotEscalation,
          active: member.active
        });
        // El usuario de un miembro no se cambia: para eso se quita y se agrega otro.
        this.form.get('userId')?.disable();
      },
      error: (err) => { this.error = err.error?.message || 'No se pudo cargar el miembro.'; }
    });
  }

  private cargarAgentes(): void {
    this.agentLookup.getAgents({}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (a) => this.agentes = a,
      error: () => this.agentes = []
    });
  }

  tieneIdioma(code: string): boolean {
    return this.idiomasSeleccionados.includes(code);
  }

  toggleIdioma(code: string): void {
    this.idiomasSeleccionados = this.tieneIdioma(code)
      ? this.idiomasSeleccionados.filter((c) => c !== code)
      : [...this.idiomasSeleccionados, code];
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (!this.teamId) return;
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    const raw = this.form.getRawValue();
    this.saving = true;
    this.error = null;
    const request = {
      userId: raw.userId as number,
      routingWeight: raw.routingWeight ?? 1,
      maxConcurrentChats: raw.maxConcurrentChats ?? 5,
      languageCodes: this.idiomasSeleccionados,
      canReceiveLiveChat: !!raw.canReceiveLiveChat,
      canReceiveBotEscalation: !!raw.canReceiveBotEscalation,
      active: !!raw.active
    };
    const obs = this.isEditing && this.memberId != null
      ? this.api.updateMember(this.teamId, this.memberId, request)
      : this.api.addMember(this.teamId, request);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (member) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Miembro', `Se actualizó a ${member.userName}.`);
        else this.toast.created('Miembro', `${member.userName} ya forma parte del equipo.`);
        this.saved.emit(member);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'No se pudo guardar el miembro.';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
